import { useEffect } from "react";
import { useReactFlow } from "@xyflow/react";
import { useHydrateAtoms } from "jotai/utils";
import { useCopilotDrawerState } from "@zapier/ai-toolkit/client";
import { useCurrentAccountId } from "@zapier/identity";
import {
  Account,
  BuiltInApp,
  Session,
  ZSL,
  accountAtom,
  canvasAtom,
  chatbotsAppAtom,
  currentAccountIdAtom,
  edgesAtom,
  interfacesAppAtom,
  isEmbedAtom,
  isPublicAtom,
  nodesAtom,
  sessionAtom,
  tablesAppAtom,
  zslAtom,
  Toggles,
  togglesAtom,
  isViewOnlyDowntimeOverrideAtom,
  RawCanvas,
  isTemplatePageAtom,
  isViewOnlyModeAtom,
  cursorAtom,
  agentsAppAtom,
  reactFlowInstanceAtom,
  copilotDrawerStateAtom,
} from "@zapier/canvas-state";
import { aiPromptAtom, showWelcomeModalAtom } from "@zapier/streaming";

import { convertCanvasSchemaForReactFlow } from "../../utils/convertCanvasSchemaForReactFlow";

export function CanvasHydrator({
  canvas: rawCanvas,
  children,
  session,
  account,
  template,
  tablesApp,
  interfacesApp,
  chatbotsApp,
  agentsApp,
  toggles,
  isPublic = false,
  isEmbed = false,
  isAiPage = false,
  prompt = "",
  isTemplatePage = false,
}: {
  canvas: RawCanvas;
  children: React.ReactNode;
  session?: Session;
  account?: Account;
  template?: ZSL;
  tablesApp: BuiltInApp;
  interfacesApp: BuiltInApp;
  chatbotsApp: BuiltInApp;
  agentsApp: BuiltInApp;
  toggles: Toggles;
  isPublic?: boolean;
  isEmbed?: boolean;
  isAiPage?: boolean;
  isTemplatePage?: boolean;
  prompt?: string;
}) {
  const isViewer = rawCanvas.access_level === "can_view"; // This will be true when is_public is true (so true for public links)

  const isViewOnlyMode =
    isAiPage ||
    isViewer ||
    isPublic ||
    isTemplatePage ||
    isEmbed ||
    toggles.viewOnlyDowntime;

  const canvas = convertCanvasSchemaForReactFlow({
    isViewOnlyMode,
    rawCanvas,
  });

  const showWelcomeModal = determineShowWelcomeModal({
    isAiPage,
    isViewOnlyMode,
    rawCanvas,
  });

  const currentAccountId = useCurrentAccountId();
  const reactFlowInstance = useReactFlow();

  const [, setIsCopilotOpen] = useCopilotDrawerState();

  useEffect(() => {
    if (isAiPage) {
      setIsCopilotOpen(true);
    }
  }, [isAiPage, setIsCopilotOpen]);

  useHydrateAtoms([
    [isTemplatePageAtom, isTemplatePage],
    [zslAtom, template],
    [canvasAtom, canvas],
    [nodesAtom, canvas.nodes],
    [edgesAtom, canvas.edges],
    [currentAccountIdAtom, currentAccountId],
    [accountAtom, account || null],
    [sessionAtom, session || null],
    [tablesAppAtom, tablesApp],
    [interfacesAppAtom, interfacesApp],
    [chatbotsAppAtom, chatbotsApp],
    [agentsAppAtom, agentsApp],
    [togglesAtom, toggles],
    [isPublicAtom, isPublic],
    [isEmbedAtom, isEmbed],
    [showWelcomeModalAtom, showWelcomeModal],
    [aiPromptAtom, prompt],
    [isViewOnlyDowntimeOverrideAtom, toggles.viewOnlyDowntime],
    [isViewOnlyModeAtom, isViewOnlyMode],
    [cursorAtom, isViewOnlyMode ? "pan" : "select"],
    /**
     * Atoms can now open/close copilot without having to pass around the useCopilotDrawerState hook.
     * Use useCopilotDrawerState hook directly when in React components, and use copilotDrawerStateAtom in atoms.
     */
    [copilotDrawerStateAtom, { setIsCopilotOpen }],
    // Store the full ReactFlow instance so atoms/hooks can access functions
    [reactFlowInstanceAtom, reactFlowInstance],
  ]);

  return children;
}

function determineShowWelcomeModal({
  isAiPage,
  isViewOnlyMode,
  rawCanvas,
}: {
  isAiPage: boolean;
  isViewOnlyMode: boolean;
  rawCanvas: RawCanvas;
}) {
  const { nodes } = rawCanvas;
  if (isAiPage) {
    return false;
  }
  if (isViewOnlyMode) {
    return false;
  }

  return (
    nodes.length < 2 &&
    nodes.every((node) => node.node_type === "default") &&
    nodes.every((node) => node.label === "") &&
    nodes.every((node) => !node.services || node.services.length === 0)
  );
}
